import { LitElement, html, css, TemplateResult } from "lit";
import { unsafeHTML } from "lit-html/directives/unsafe-html.js";
import { customElement, property, state, query } from "lit/decorators.js";
import "@shoelace-style/shoelace/dist/components/icon-button/icon-button.js";
import "@shoelace-style/shoelace/dist/components/details/details.js";
import { Reading, MonitoringOverviewItem } from "../../endpoints/endpoint_types.js";

const PRIORITY_MEDIUM = 500;
const PRIORITY_LOW = 200;

/**
 * Display monitor details for a single monitored URL.
 */
@customElement("monitor-details")
export class MonitorDetails extends LitElement {
    @property({ type: Object }) reading: MonitoringOverviewItem | null = null;

    @property({ type: Object }) error: TemplateResult | null = null;

    @query("monitor-details-days") detailsDaysComponent!: MonitorDetailsDays;

    render() {
        if (this.error) {
            return html`
                <sl-alert variant="danger">
                    <strong>Error:</strong>
                    ${this.error}
                </sl-alert>`;
        }

        if (!this.reading) {
            return html`
                <sl-alert variant="warning">
                    No reading data available.
                </sl-alert>`;
        }

        const onDelete = () => this.deleteUrl(this.reading!.url);
        const onNavigate = () => window.open(this.reading!.url, "_blank");

        const latestPrice = this.reading.price;
        const medianPrice = this.reading.median_price;

        const isSale = latestPrice && latestPrice < medianPrice;
        const isSnoozed = this.reading.priority < PRIORITY_MEDIUM;

        return html`
            <sl-details class=${isSale && !isSnoozed ? "sale" : ""} @sl-show=${this.onDetailsShow}>
                <div slot="summary">
                    <sl-icon-button
                        name="trash"
                        label="Delete"
                        @click=${onDelete}>
                    </sl-icon-button>
                    <sl-icon-button
                        name=${isSnoozed ? "bell" : "moon"}
                        label=${isSnoozed ? "Unsnooze" : "Snooze"}
                        @click=${this.onSnoozeClick}>
                    </sl-icon-button>
                    <a href="${this.reading.url}" target="_blank">
                        ${this.reading?.title}
                        <sl-icon-button
                            name="box-arrow-up-right"
                            label="navigate to item"
                            @click=${onNavigate}>
                        </sl-icon-button>
                    </a>
                    <span>
                        Now: ${latestPrice?.toFixed(2) || "N/A"}
                    </span>
                    <span>
                        Median: ${medianPrice?.toFixed(2) || "N/A"}
                    </span>
                </div>
                <monitor-details-days .urlKey=${this.reading?.url_key}></monitor-details-days>
            </sl-details>`;
    }

    onDetailsShow() {
        if (this.detailsDaysComponent) {
            this.detailsDaysComponent.loadReadings();
        }
    }

    async onSnoozeClick(e: Event) {
        e.stopPropagation();
        if (!this.reading) return;

        const isSnoozed = this.reading.priority < PRIORITY_MEDIUM;
        const newPriority = isSnoozed ? PRIORITY_MEDIUM : PRIORITY_LOW;

        try {
            const response = await fetch("./data/setpriority", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ url: this.reading.url, priority: newPriority }),
            });
            if (!response.ok) {
                console.error("Failed to set priority:", response.statusText);
                return;
            }
            this.dispatchEvent(new CustomEvent("listing-priority-changed", { bubbles: true, composed: true }));
        } catch (error) {
            console.error("Failed to set priority:", error);
        }
    }

    async deleteUrl(target: string) {
        console.log("Deleting URL:", target);
        try {
            const response = await fetch("./data/removemonitor", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ url: target }),
            });
            if (!response.ok) {
                const errorBody = await response.text();
                this.error = html`<strong>
                    <div>Failed to delete URL.</div>
                    <div>${response.status} ${response.statusText}</div>
                </strong>
                <div>${unsafeHTML(errorBody)}</div>`;
                return;
            }
            // Mark as dirty to trigger reload in updated()
            this.reading = null;
        } catch (error) {
            console.error("Failed to delete URL:", error);
            this.error = html`<div>Failed to delete URL: ${String(error)}</div>`;
        }
    }

    static styles = css`
        sl-icon-button[name="trash"] {
            color: red;
        }

        sl-details div[slot="summary"] {
            display: flex;
            gap: 20px;
        }

         sl-details.sale::part(base) {
            text-decoration: underline;
            background-color: #fffde2;
         }
  `;
}

@customElement("monitor-details-days")
export class MonitorDetailsDays extends LitElement {
    @property({ type: String }) urlKey: string | null = null;

    @state() readings: Reading[] = [];

    async loadReadings() {
        if (this.urlKey) {
            this.readings = await this.fetchReadings(this.urlKey);
        }
    }

    async fetchReadings(urlKey: string): Promise<Reading[]> {
        const response = await fetch(`./data/readinghistory/${encodeURIComponent(urlKey)}`);
        if (!response.ok) {
            console.error("Failed to fetch readings:", response.statusText);
            return [];
        }
        const data: { readings: Reading[]; } = await response.json();
        return data.readings;
    }

    render() {
        if (!this.urlKey) {
            return html`<div>No URL</div>`;
        }

        return html`
            <table>
                <tr>
                    <th>Date</th>
                    <th>Reading</th>
                </tr>
                ${this.readings.map(renderDay)}
            </table>`;
    }

    static styles = css`
        table {
            border-collapse: separate;
            border-spacing: 0 12px;
            width: 100%;
        }

        tr {
            border-bottom: 1px solid #ddd;
        }

        tr:last-child {
            border-bottom: none;
        }

        td, th {
            padding: 8px 12px;
            text-align: left;
        }
  `;
}

function renderDay(reading: Reading) {
    console.log("Rendering day:", reading.date.toString(), reading.price);
    const formattedDate = new Date(reading.date).toLocaleDateString();
    return html`
        <tr>
            <td>
                ${formattedDate}
            </td>
            <td>
                ${reading.price?.toFixed(2)}
            </td>
            <td>
                <sl-icon-button
                    name="box-arrow-up-right"
                    label="Open screenshot"
                    @click=${() => window.open(reading.screenshot_download, "_blank")}>
                </sl-icon-button>
            </td>
        </tr>
    `;
}

